import { useState, useEffect, useRef } from 'react';
import EntitlementsService from '../services/EntitlementsService';
import type { EntityEntitlementForTargetResponse } from '../types/EntityEntitlements';

export interface UseEntityUpsertTier3Params {
    open: boolean;
    entityId: string | null | undefined;
    entityType: string | undefined;
    isEditMode: boolean;
    targetEntitlementProp?: EntityEntitlementForTargetResponse | null;
}

/** Tier3 entitlement fetch/cache for GenericEntityUpsertDialog edit mode. */
export function useEntityUpsertTier3({
    open,
    entityId,
    entityType,
    isEditMode,
    targetEntitlementProp,
}: UseEntityUpsertTier3Params) {
    const [targetEntitlementState, setTargetEntitlementState] =
        useState<EntityEntitlementForTargetResponse | null>(null);
    const [tier3Settled, setTier3Settled] = useState(false);
    const [tier3EntitlementError, setTier3EntitlementError] = useState<string | null>(null);

    const tier3FetchInFlightRef = useRef(false);
    const tier3ResultRef = useRef<EntityEntitlementForTargetResponse | null>(null);
    const tier3ResultEntityKeyRef = useRef('');
    const tier3CacheByKeyRef = useRef<Map<string, EntityEntitlementForTargetResponse | null>>(new Map());
    const tier3PromiseByKeyRef = useRef<Map<string, Promise<EntityEntitlementForTargetResponse | null>>>(
        new Map()
    );

    const targetEntitlement =
        targetEntitlementProp !== undefined ? targetEntitlementProp : targetEntitlementState;

    useEffect(() => {
        if (!open) {
            setTargetEntitlementState(null);
            setTier3Settled(false);
            setTier3EntitlementError(null);
            tier3FetchInFlightRef.current = false;
            tier3ResultRef.current = null;
            tier3ResultEntityKeyRef.current = '';
            tier3CacheByKeyRef.current.clear();
            tier3PromiseByKeyRef.current.clear();
        }
    }, [open]);

    useEffect(() => {
        if (open && isEditMode && targetEntitlementProp !== undefined) {
            setTier3Settled(true);
        }
    }, [open, isEditMode, targetEntitlementProp]);

    useEffect(() => {
        if (!open || !entityId || !entityType) {
            if (!open || !entityId) {
                setTargetEntitlementState(null);
                setTier3Settled(false);
                tier3FetchInFlightRef.current = false;
            }
            return;
        }
        if (targetEntitlementProp !== undefined) {
            return;
        }
        const entityKey = `${entityType.trim().toLowerCase()}-${entityId}`;
        const cached = tier3CacheByKeyRef.current.get(entityKey);
        if (cached !== undefined) {
            tier3ResultRef.current = cached;
            tier3ResultEntityKeyRef.current = entityKey;
            setTargetEntitlementState(cached);
            setTier3Settled(true);
            return;
        }
        if (tier3ResultRef.current != null && tier3ResultEntityKeyRef.current === entityKey) {
            setTier3Settled(true);
            return;
        }
        const schemaEntityId = entityType.trim().toLowerCase();
        let promise = tier3PromiseByKeyRef.current.get(entityKey);
        if (!promise) {
            tier3FetchInFlightRef.current = true;
            setTier3Settled(false);
            promise = EntitlementsService.fetchEntityEntitlementForTarget(schemaEntityId, entityId)
                .then((entitlement) => entitlement ?? null)
                .finally(() => {
                    tier3FetchInFlightRef.current = false;
                    tier3PromiseByKeyRef.current.delete(entityKey);
                });
            tier3PromiseByKeyRef.current.set(entityKey, promise);
        }
        let cancelled = false;
        promise
            .then((result: EntityEntitlementForTargetResponse | null) => {
                if (!cancelled) {
                    tier3ResultRef.current = result;
                    tier3ResultEntityKeyRef.current = entityKey;
                    tier3CacheByKeyRef.current.set(entityKey, result);
                    setTargetEntitlementState(result);
                    setTier3EntitlementError(null);
                    setTier3Settled(true);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    console.error(
                        `Selector entitlement (tier3) failed for entityType "${schemaEntityId}", entityId "${entityId}":`,
                        err
                    );
                    tier3ResultRef.current = null;
                    tier3CacheByKeyRef.current.set(entityKey, null);
                    setTargetEntitlementState(null);
                    setTier3EntitlementError(err instanceof Error ? err.message : String(err));
                    setTier3Settled(true);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [open, entityId, entityType, targetEntitlementProp]);

    return {
        targetEntitlement,
        tier3Settled,
        tier3EntitlementError,
        tier3ResultRef,
    };
}
